<template>
  <ak-masthead-item
    v-click-outside="closeMenu"
    tag="div"
    class="ak-masthead__dropdown"
    :class="{ 'is-open': newActive }"
    @click.prevent="showMenu"
  >
    <template v-if="label">
      <ak-pack class="ak-masthead__dropdown-item h-full" spacing="tiny">
        <ak-pack-item grow>
          {{ label }}
        </ak-pack-item>
        <ak-pack-item>
          <ak-icon type="chevron-down" size="small" />
        </ak-pack-item>
      </ak-pack>
    </template>
    <div v-else class="ak-masthead__dropdown-item">
      <slot name="customLabel" />
    </div>
    <transition name="fade-down-fast">
      <div v-show="newActive" class="ak-masthead__dropdown-menu">
        <slot />
      </div>
    </transition>
  </ak-masthead-item>
</template>

<script>
import { defineComponent, ref, watch } from 'vue'
import clickOutside from '../../directives/clickOutside'
import MastheadItem from './MastHeadItem.vue'
import Pack from '../pack/Pack.vue'
import PackItem from '../pack/PackItem.vue'
import Icon from '../icon/Icon.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-masthead-dropdown>
   *   <ak-masthead-item></ak-masthead-item>
   * </ak-masthead-dropdown>
   */
  name: 'AkMastheadDropdown',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [MastheadItem.name]: MastheadItem,
    [PackItem.name]: PackItem,
    [Pack.name]: Pack,
    [Icon.name]: Icon,
  },

  /**
   * @type {Object}
   * @description A collection of directives that this component is
   * dependant on.
   */
  directives: {
    clickOutside,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Boolean}
     * @description the active state of the link.
     */
    active: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description the label for the mast head dropdown link.
     */
    label: {
      default: undefined,
      type: String,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const isNavDropdown = ref(true)
    const newActive = ref(props.active)

    /**
     * @description Opens the menu
     */
    const showMenu = () => {
      newActive.value = true
    }

    /**
     * @description Closes the menu
     */
    const closeMenu = () => {
      newActive.value = false
    }

    watch(
      () => props.active,
      (newVal) => {
        newActive.value = newVal
      },
    )

    return {
      newActive,
      isNavDropdown,
      showMenu,
      closeMenu,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-masthead__dropdown {
  position: relative;
  @media screen and #{$mq-nav} {
    align-items: flex-start;
    flex-direction: column;
  }
  &-item {
    @media screen and #{$mq-nav} {
      padding: $spacing-small;
      width: 100%;
    }
  }
  &-menu {
    width: 100%;
    display: block;
    @media screen and #{$mq-nav-min} {
      position: absolute;
      top: 100%;
      left: 0;
      width: 200px;
      padding-top: $spacing-tiny;
      padding-bottom: $spacing-tiny;
      background-color: $color-grey-darker;
      box-shadow: $base-shadow;
      border-bottom-left-radius: $base-border-radius;
      border-bottom-right-radius: $base-border-radius;
    }
    a {
      color: rgba(255, 255, 255, 0.8);
      padding: $spacing-tiny $spacing-small;
      display: block;

      &:hover,
      &:focus {
        color: #fff;
      }
      &:focus {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }
  }
}
</style>
